<script setup lang="ts">
import { SiteStatus } from "#imports"

type CommisisioningDetails = {
  has_passed_performance_test: boolean
  has_work_acceptance: boolean
  is_handed_over: boolean
}
const props = defineProps<{
  status: SiteStatus | undefined | null
  commissioning_status?: CommisisioningDetails | undefined | null
  iconOnly?: boolean
}>()

const { t } = useI18n()
const statusLabel = computed(() =>
  t(props.status ? `site.status.${props.status}` : "invoice.status.informative"),
)

const fallbackBadge = { icon: "i-fa6-solid-question", color: "gray" as UIColor }

const badgeMap: Record<SiteStatus, { icon: string; color: UIColor }> = {
  [SiteStatus.enum.no_status]: {
    icon: "i-fa6-solid-question",
    color: "gray",
  },
  [SiteStatus.enum.development]: {
    icon: "i-fa6-solid-list-check",
    color: "rnxblue",
  },
  [SiteStatus.Enum.financing]: {
    icon: "i-fa6-solid-hand-holding-dollar",
    color: "rnxblue",
  },
  [SiteStatus.Enum.connection_requested]: {
    icon: "i-fa6-solid-plug",
    color: "rnxblue",
  },
  [SiteStatus.Enum.construction]: {
    icon: "i-fa6-solid-hammer",
    color: "rnxblue",
  },
  [SiteStatus.Enum.waiting_commissioning]: {
    icon: "i-fa6-solid-plug-circle-exclamation",
    color: "rnxorange",
  },
  [SiteStatus.Enum.commissioned]: {
    icon: "i-fa6-solid-plug-circle-bolt",
    color: "rnxgreen",
  },
  [SiteStatus.Enum.test_passed]: {
    icon: $icons.bolt,
    color: "rnxgreen",
  },
  [SiteStatus.Enum.test_failed]: {
    icon: $icons.bolt,
    color: "red",
  },
  [SiteStatus.Enum.added_to_warrant]: {
    icon: "i-fa6-solid-file-signature",
    color: "rnxorange",
  },
  [SiteStatus.enum.work_acceptance]: {
    icon: "i-material-symbols-fact-check-outline",
    color: "rnxblue",
  },
  [SiteStatus.Enum.handed_over]: {
    icon: "i-fa6-solid-check-double",
    color: "rnxgreen",
  },
  [SiteStatus.Enum.handed_over_with_reserve]: {
    icon: "i-fa6-solid-check-double",
    color: "rnxorange",
  },
  [SiteStatus.Enum.on_hold]: {
    icon: "i-fa6-solid-hand",
    color: "rnxorange",
  },
  [SiteStatus.Enum.dismanteled]: {
    icon: "i-fa6-solid-trash-can",
    color: "gray",
  },
  [SiteStatus.Enum.sold]: {
    icon: "i-fa6-regular-handshake",
    color: "gray",
  },
  [SiteStatus.Enum.capacity_change]: {
    icon: "i-fa6-solid-arrows-up-down",
    color: "rnxorange",
  },
  [SiteStatus.Enum.damaged]: {
    icon: $icons.triangle_warning,
    color: "red",
  },
  [SiteStatus.Enum.repaired]: {
    icon: "i-fa6-solid-screwdriver-wrench",
    color: "rnxgreen",
  },
}
</script>

<template>
  <Badge
    v-if="iconOnly"
    v-tippy="statusLabel"
    :color="status ? (badgeMap[status] ?? fallbackBadge).color : 'gray'"
    role="img"
    :aria-label="statusLabel"
  >
    <UIcon
      :name="status ? (badgeMap[status] ?? fallbackBadge).icon : 'i-fa6-solid-circle-info'"
      aria-hidden="true"
    />
  </Badge>
  <div v-else-if="status">
    <Badge :color="(badgeMap[status] ?? fallbackBadge).color">
      <div
        v-if="commissioning_status"
        class="inline-flex max-w-full items-center gap-x-2 rounded-md px-1"
      >
        <UIcon :name="(badgeMap[status] ?? fallbackBadge).icon" />
        <span v-overflow class="truncate text-xs font-semibold">
          {{ $t(`site.status.${status}`) }}
        </span>
        <UIcon v-if="commissioning_status.is_handed_over" name="i-fa6-solid-check-double" />
        <div v-else class="flex flex-row items-center gap-x-1">
          <UIcon
            v-if="commissioning_status.has_passed_performance_test"
            v-tippy="$t(`site.status.${SiteStatus.enum.test_passed}`)"
            color="green"
            :name="$icons.bolt"
          />
          <UIcon
            v-if="commissioning_status.has_work_acceptance"
            v-tippy="$t(`site.status.${SiteStatus.enum.work_acceptance}`)"
            name="i-material-symbols-fact-check-outline"
          />
        </div>
      </div>
      <div v-else class="inline-flex max-w-full items-center gap-x-2 rounded-md px-1">
        <UIcon :name="(badgeMap[status] ?? fallbackBadge).icon" />
        <span v-overflow class="truncate text-xs font-semibold">
          {{ $t(`site.status.${status}`) }}
        </span>
      </div>
    </Badge>
  </div>
  <Badge v-else color="gray" class="inline-flex max-w-full items-center gap-x-2 px-1">
    <UIcon name="i-fa6-solid-circle-info" />
    <span v-overflow class="truncate text-xs font-semibold">
      {{ $t("invoice.status.informative") }}
    </span>
  </Badge>
</template>
